import { ImageIcon, XIcon } from "@phosphor-icons/react";
import { type ReactNode, useRef } from "react";
import { FileTrigger } from "react-aria-components";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/field";
import { IconButton } from "@/components/ui/icon-button";
import { useFileDropZone } from "@/hooks/useFileDropZone";
import { cn } from "@/lib/utils";
import {
  LOGO_UPLOAD_ACCEPTED_FILE_TYPES,
  LOGO_UPLOAD_HELPER_TEXT,
  validateAndSelectLogoFile,
} from "./logoUploadHelpers";

export interface LogoUploadSectionProps {
  previewSrc: string | null;
  fileName: string | null;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  isDisabled?: boolean;
}

const LogoUploadSection = ({
  previewSrc,
  fileName,
  onFileSelect,
  onRemove,
  isDisabled = false,
}: LogoUploadSectionProps) => {
  const uploadTriggerRef = useRef<HTMLButtonElement>(null);

  const blurUploadTrigger = () => {
    uploadTriggerRef.current?.blur();
  };

  const handleFileSelect = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;

    const isValid = await validateAndSelectLogoFile(file);
    if (!isValid) return;

    onFileSelect(file);
  };

  const { isDragOver, dropZoneProps } = useFileDropZone((files) => {
    void handleFileSelect(files);
  }, isDisabled);

  const dropZoneHighlightClassName = isDragOver
    ? "border-violet-400 bg-violet-50/60 ring-2 ring-violet-200"
    : undefined;

  const uploadTrigger = (children: ReactNode) => (
    <FileTrigger
      acceptedFileTypes={LOGO_UPLOAD_ACCEPTED_FILE_TYPES}
      allowsMultiple={false}
      onSelect={(fileList) => {
        blurUploadTrigger();
        void handleFileSelect(fileList);
      }}
    >
      {children}
    </FileTrigger>
  );

  return (
    <div
      className={cn(
        "flex flex-col gap-1",
        isDisabled && "pointer-events-none opacity-50",
      )}
    >
      <Label aria-label="logo-upload-label" className="text-sm text-gray-700">
        Add a logo
      </Label>
      <p className="text-xs text-gray-600">{LOGO_UPLOAD_HELPER_TEXT}</p>

      {previewSrc ? (
        <div
          {...dropZoneProps}
          className={cn(
            "mt-2 flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3",
            dropZoneHighlightClassName,
          )}
        >
          <img
            src={previewSrc}
            alt={fileName ? `${fileName} preview` : "Logo preview"}
            className="size-16 shrink-0 rounded-md border border-slate-200 bg-white object-contain"
          />
          <div className="min-w-0 flex-1">
            <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-gray-900">
              {fileName ?? "Logo"}
            </p>
            <p className="text-xs text-gray-600">PNG or JPG</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {uploadTrigger(
              <Button
                ref={uploadTriggerRef}
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Replace logo"
                isDisabled={isDisabled}
              >
                Replace
              </Button>,
            )}
            <IconButton
              variant="icon-only"
              icon={<XIcon size={16} />}
              onPressChange={onRemove}
              aria-label="Remove logo"
              className="text-gray-700"
              isDisabled={isDisabled}
            />
          </div>
        </div>
      ) : (
        <div
          {...dropZoneProps}
          className={cn("mt-2 rounded-md", dropZoneHighlightClassName)}
        >
          {uploadTrigger(
            <Button
              ref={uploadTriggerRef}
              type="button"
              variant="ghost"
              aria-label="Upload logo"
              isDisabled={isDisabled}
              className={cn(
                "h-auto w-full flex-col gap-2 rounded-md border border-dashed border-slate-300 bg-white px-4 py-8",
                "hover:border-violet-400 hover:bg-violet-50/40",
                isDragOver && "border-violet-400 bg-violet-50/40",
              )}
            >
              <span className="flex size-10 items-center justify-center rounded-full bg-violet-50">
                <ImageIcon size={20} className="text-violet-800" />
              </span>
              <span className="text-sm font-medium text-gray-900">
                Click to upload or drag and drop
              </span>
              <span className="text-xs font-normal text-gray-600">
                PNG or JPG, up to 25 MB
              </span>
            </Button>,
          )}
        </div>
      )}
    </div>
  );
};

export default LogoUploadSection;
